import { useState, useEffect } from 'react';
import { FiX, FiSave, FiTag } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Modal para agregar/editar notas bíblicas
 */
const BibleNoteModal = ({
  isOpen,
  onClose,
  onSave,
  reference = '',
  book = '',
  chapter = 0,
  verses = [],
  initialNote = null,
  user: propUser,
  modulePermissions = []
}) => {
  const [noteContent, setNoteContent] = useState(initialNote?.content || '');
  const [noteTitle, setNoteTitle] = useState(initialNote?.title || '');
  const [noteColor, setNoteColor] = useState(initialNote?.color || '#3B82F6');
  const [keywords, setKeywords] = useState(initialNote?.keywords || '');
  const [selectedCategories, setSelectedCategories] = useState(initialNote?.categories || []);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(true);
  
  const { user: contextUser } = useAuth();
  const user = propUser || contextUser;

  // Actualizar estados cuando cambia initialNote
  useEffect(() => {
    if (initialNote) {
      setNoteContent(initialNote.content || '');
      setNoteTitle(initialNote.title || '');
      setNoteColor(initialNote.color || '#3B82F6');
      setKeywords(typeof initialNote.keywords === 'string' ? initialNote.keywords : '');
      // Si initialNote tiene categories como array de objetos, extraer los IDs
      // Si tiene categoryIds como array de números, usarlo directamente
      const catIds = initialNote.categories?.map(c => c.id || c) || initialNote.categoryIds || [];
      setSelectedCategories(catIds);
    } else {
      // Limpiar cuando no hay initialNote (nueva nota)
      setNoteContent('');
      setNoteTitle('');
      setNoteColor('#3B82F6');
      setKeywords('');
      setSelectedCategories([]);
    }
  }, [initialNote]);

  // Cargar categorías desde el backend
  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const { default: api } = await import('../../utils/api');
      const response = await api.get('/categorias');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando categorías:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!noteContent.trim()) {
      alert('Por favor escribe una nota');
      return;
    }

    if (!user) {
      alert('Debes iniciar sesión');
      return;
    }

    setSaving(true);
    try {
      const { BibleStorageService } = await import('../../utils/bibleStorageHelper');
      const bibleStorage = new BibleStorageService(user, modulePermissions);

      const noteData = {
        title: noteTitle || 'Sin título',
        content: noteContent,
        book: book,
        chapter: chapter,
        verseStart: Math.min(...verses),
        verseEnd: Math.max(...verses),
        color: noteColor,
        keywords: keywords,
        categoryIds: selectedCategories.map(cat => cat.value).filter(id => id != null), // Extraer solo los IDs válidos
        isPublic: false
      };

      if (initialNote?.id) {
        await bibleStorage.updateNote(initialNote.id, noteData);
      } else {
        await bibleStorage.addNote(noteData);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error guardando nota:', error);
      console.error('Error completo:', error.response?.data);
      alert(`Error al guardar la nota: ${error.response?.data?.message || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Configuración de ReactQuill
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link'],
      ['clean']
    ]
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {initialNote ? 'Editar Nota' : 'Nueva Nota Bíblica'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">{reference}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Título y Color */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título (opcional)
              </label>
              <input
                type="text"
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
                placeholder="Ej: Reflexión personal, Estudio teológico..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color
              </label>
              <div className="flex gap-2">
                {['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'].map(color => (
                  <button
                    key={color}
                    onClick={() => setNoteColor(color)}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      noteColor === color ? 'border-gray-800 scale-110' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Palabras Clave */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiTag className="inline w-4 h-4 mr-1" />
              Palabras Clave (separadas por comas)
            </label>
            <input
              type="text"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="Ej: fe, salvación, gracia"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Categorías */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Categorías (puedes seleccionar varias)
            </label>
            {loadingCategories ? (
              <p className="text-sm text-gray-500">Cargando categorías...</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {categories.map(cat => (
                  <label
                    key={cat.id}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(cat.id)}
                      onChange={() => toggleCategory(cat.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{cat.nombre}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Editor de contenido */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contenido <span className="text-red-500">*</span>
            </label>
            <ReactQuill
              theme="snow"
              value={noteContent}
              onChange={setNoteContent}
              modules={modules}
              placeholder="Escribe tu nota aquí..."
              className="bg-white"
              style={{ height: '250px', marginBottom: '50px' }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <span className="inline-block w-2 h-2 bg-purple-500 rounded-full"></span>
            Guardado localmente
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <FiSave className="w-5 h-5" />
              {saving ? 'Guardando...' : 'Guardar Nota'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BibleNoteModal;


